import { confirm, toast } from "@devhop/ui";
import { useCallback, useState } from "react";
import type { QiStockShortfallDTO } from "../api/catalog-types";
import { quotationInvoiceApi } from "../api/client";

type IssueStockCheck = {
  ok: boolean;
  shortfalls: QiStockShortfallDTO[];
};

type PendingIssue = {
  id: string;
  documentNumber: string;
  onIssue: () => void;
};

export function useQuotationIssueWithPrecheck() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkResult, setCheckResult] = useState<IssueStockCheck | null>(null);
  const [pending, setPending] = useState<PendingIssue | null>(null);

  const reset = useCallback(() => {
    setOpen(false);
    setLoading(false);
    setCheckResult(null);
    setPending(null);
  }, []);

  const requestIssue = useCallback(
    async (params: {
      id: string;
      documentNumber: string;
      documentType: string;
      onIssue: () => void;
    }) => {
      if (params.documentType !== "quotation") {
        const ok = await confirm({
          title: "ອອກເອກະສານ",
          description: `ຢືນຢັນອອກເອກະສານ #${params.documentNumber} ແລ້ວ? ຫຼັງອອກແລ້ວຈະບໍ່ແກ້ໄຂໄດ້ງ່າຍຄືຮ່າງ.`,
          actionText: "ອອກເອກະສານ",
        });
        if (ok) params.onIssue();
        return;
      }

      setPending({
        id: params.id,
        documentNumber: params.documentNumber,
        onIssue: params.onIssue,
      });
      setCheckResult(null);
      setOpen(true);
      setLoading(true);

      try {
        const result = await quotationInvoiceApi.checkIssueStock(params.id);
        setCheckResult(result);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "ກວດສຕັອກກ່ອນອອກໃບລົ້ມເຫຼວ",
        );
        reset();
      } finally {
        setLoading(false);
      }
    },
    [reset],
  );

  const confirmIssue = useCallback(() => {
    if (!pending || !checkResult?.ok) return;
    const issue = pending.onIssue;
    reset();
    issue();
  }, [pending, checkResult?.ok, reset]);

  return {
    requestIssue,
    precheckDialogProps: {
      open,
      onOpenChange: (next: boolean) => {
        if (!next) reset();
        else setOpen(true);
      },
      loading,
      documentNumber: pending?.documentNumber ?? "",
      checkResult,
      onConfirmIssue: confirmIssue,
    },
  };
}
